import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import {
  useUpdateLiftStatus,
  type Lift,
  type MaintenanceStatus,
} from '@/hooks';
import { useNotify } from '@/hooks/useNotify';
import { maintenanceStatusLabel } from '@/utils/liftLabels';

interface MaintenanceStatusDialogProps {
  open: boolean;
  lift: Lift;
  onClose: () => void;
}

interface FormData {
  maintenanceStatus: MaintenanceStatus;
  notes: string;
  lastMaintenanceAt: string;
  nextMaintenanceAt: string;
}

function formatDateTimeLocal(isoString: string | null): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toISOString().slice(0, 16);
}

function parseDateTimeLocal(value: string): string | null {
  if (!value) return null;
  return new Date(value).toISOString();
}

export function MaintenanceStatusDialog({
  open,
  lift,
  onClose,
}: MaintenanceStatusDialogProps) {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const [formData, setFormData] = useState<FormData>({
    maintenanceStatus: lift.maintenanceStatus,
    notes: lift.notes ?? '',
    lastMaintenanceAt: formatDateTimeLocal(lift.lastMaintenanceAt),
    nextMaintenanceAt: formatDateTimeLocal(lift.nextMaintenanceAt),
  });

  const [isDirty, setIsDirty] = useState(false);
  const updateMutation = useUpdateLiftStatus();

  useEffect(() => {
    setFormData({
      maintenanceStatus: lift.maintenanceStatus,
      notes: lift.notes ?? '',
      lastMaintenanceAt: formatDateTimeLocal(lift.lastMaintenanceAt),
      nextMaintenanceAt: formatDateTimeLocal(lift.nextMaintenanceAt),
    });
    setIsDirty(false);
  }, [lift, open]);

  const handleSubmit = async () => {
    try {
      await updateMutation.mutateAsync({
        id: lift.id,
        data: {
          maintenanceStatus: formData.maintenanceStatus,
          notes: formData.notes || null,
          lastMaintenanceAt: parseDateTimeLocal(formData.lastMaintenanceAt),
          nextMaintenanceAt: parseDateTimeLocal(formData.nextMaintenanceAt),
        },
      });
      notify.success(
        t('maintenance.statusUpdated'),
        t('maintenance.statusUpdatedDesc', { name: lift.name, status: maintenanceStatusLabel(t, formData.maintenanceStatus) })
      );
      onClose();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('maintenance.updateFailed') }));
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const statusChanged = formData.maintenanceStatus !== lift.maintenanceStatus;
  const isOutOfService = formData.maintenanceStatus === 'out_of_service';

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('maintenance.title')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      isLoading={updateMutation.isPending}
      submitLabel={t('maintenance.updateStatus')}
      submitColor={statusChanged && isOutOfService ? 'warning' : 'primary'}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {isOutOfService && (
            <Alert severity="warning">
              {t('maintenance.outOfServiceWarning')}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel>{tc('status')}</InputLabel>
            <Select
              value={formData.maintenanceStatus}
              label={tc('status')}
              onChange={(e) =>
                { handleChange('maintenanceStatus', e.target.value as MaintenanceStatus); }
              }
            >
              <MenuItem value="operational">{t('lifts.statusOperational')}</MenuItem>
              <MenuItem value="needs_maintenance">{t('lifts.statusNeedsMaintenance')}</MenuItem>
              <MenuItem value="out_of_service">{t('lifts.statusOutOfService')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={t('maintenance.lastMaintenance')}
            type="datetime-local"
            value={formData.lastMaintenanceAt}
            onChange={(e) => { handleChange('lastMaintenanceAt', e.target.value); }}
            fullWidth
            helperText={t('maintenance.lastMaintenanceHint')}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />

          <TextField
            label={t('maintenance.nextMaintenance')}
            type="datetime-local"
            value={formData.nextMaintenanceAt}
            onChange={(e) => { handleChange('nextMaintenanceAt', e.target.value); }}
            fullWidth
            helperText={t('maintenance.nextMaintenanceHint')}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />

          <TextField
            label={t('liftForm.notes')}
            value={formData.notes}
            onChange={(e) => { handleChange('notes', e.target.value); }}
            fullWidth
            multiline
            rows={3}
            placeholder={t('maintenance.notesPlaceholder')}
            helperText={t('maintenance.notesHint')}
          />
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
